import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import type { PrivateMoneyFormValues } from '..';
import FormErrorMessage from '../../../atoms/FormErrorMessage';
import Grid from '../../../atoms/Grid';
import Label from '../../../atoms/Label';
import Required from '../../../atoms/Required';
import TextBox from '../../../atoms/TextBox';
import ToggleSwitch from '../../../atoms/ToggleSwitch';

const FormGrid = styled(Grid.Container)`
  ${Label} {
    margin-bottom: 0;
    padding: 5px 0;
    font-weight: bold;
    color: #676767;
  }

  ${Grid.Divider} {
    margin: 12px 0;
  }
`;

const ToggleSwitchLabel = styled(Label)`
  margin-left: 1.25rem;
  color: #aaaaaa !important;
`;

const AdditionalSettingsForm = ({
  allowedInputs = 'all',
}: {
  allowedInputs?: 'all' | (keyof PrivateMoneyFormValues)[];
}) => {
  const { control, watch } = useFormContext<PrivateMoneyFormValues>();

  const isInputAllowed = (name: keyof PrivateMoneyFormValues) =>
    allowedInputs === 'all' || allowedInputs?.includes(name);

  return (
    <FormGrid>
      <Grid.Item size={2}>
        <Label>抽選機能サービス連携</Label>
      </Grid.Item>
      <Grid.Item size={4} alignCenter gap={10}>
        <Controller
          name="is_topup_quota_available"
          control={control}
          render={({ field: { onChange, value } }) => (
            <ToggleSwitch
              checked={value}
              onChange={onChange}
              disabled={!isInputAllowed('is_topup_quota_available')}
              checkedText=""
              uncheckedText=""
            />
          )}
        />
        <Label style={{ marginLeft: '1.25rem', color: '#aaaaaa' }}>
          抽選機能サービス連携を有効にする
        </Label>
      </Grid.Item>
      <Grid.Divider />
      <Grid.Item size={2}>
        <Label>Webhook</Label>
      </Grid.Item>
      <Grid.Item size={8} container gridSize={2} columnGap={32} rowGap={8}>
        <Grid.Item alignCenter>
          <Controller
            name="use_webhook_callback_url"
            control={control}
            render={({ field: { onChange, value } }) => (
              <ToggleSwitch
                checked={value}
                onChange={onChange}
                disabled={!isInputAllowed('use_webhook_callback_url')}
                checkedText=""
                uncheckedText=""
              />
            )}
          />
          <ToggleSwitchLabel>決済ごとのWebhook通知を有効にする</ToggleSwitchLabel>
        </Grid.Item>
        <Grid.Item nextRow flexDirection="column">
          <Label>
            Webhook URL <Required />
          </Label>
          <Controller
            name="transaction_webhook_callback_url"
            control={control}
            rules={{
              validate: (value) => {
                if (!watch('use_webhook_callback_url')) return true;
                if (!/^https?:\/\//.test(value ?? '')) return 'Webhook URLを入力してください';
                return true;
              },
            }}
            render={({ field: { onChange, value } }) => (
              <TextBox
                syncValue={value ?? ''}
                fullWidth
                disabled={
                  !isInputAllowed('transaction_webhook_callback_url') ||
                  !watch('use_webhook_callback_url')
                }
                placeholder="https://"
                onChange={onChange}
              />
            )}
          />
          <FormErrorMessage name="transaction_webhook_callback_url" />
        </Grid.Item>
      </Grid.Item>

      <Grid.Divider />

      <Grid.Item size={2}>
        <Label>マイナンバーカード認証</Label>
      </Grid.Item>
      <Grid.Item size={8} container gridSize={2} columnGap={32} rowGap={8}>
        <Grid.Item alignCenter>
          <Controller
            name="use_itrust_tenent"
            control={control}
            render={({ field: { onChange, value } }) => (
              <ToggleSwitch
                checked={value}
                onChange={onChange}
                disabled={!isInputAllowed('use_itrust_tenent')}
                checkedText=""
                uncheckedText=""
              />
            )}
          />
        </Grid.Item>
        <Grid.Item nextRow flexDirection="column">
          <Label>
            テナント識別子
            <Required />
          </Label>
          <Controller
            name="itrust_tenant_code"
            control={control}
            rules={{
              validate: (value) => {
                if (!watch('use_itrust_tenent')) return true;
                if (watch('use_itrust_tenent') && !value) return 'テナント識別子を入力してください';
                return true;
              },
            }}
            render={({ field: { onChange, value } }) => (
              <TextBox
                syncValue={value ?? ''}
                fullWidth
                disabled={!isInputAllowed('itrust_tenant_code') || !watch('use_itrust_tenent')}
                onChange={(e) => /^[a-zA-Z0-9]*$/.test(e.target.value) && onChange(e)}
              />
            )}
          />
          <FormErrorMessage name="itrust_tenant_code" />
        </Grid.Item>
        <Grid.Item nextRow flexDirection="column">
          <Label>住民の定義</Label>
          <Controller
            name="resident_address_pattern"
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextBox
                syncValue={value ?? ''}
                fullWidth
                disabled={!isInputAllowed('resident_address_pattern') || !watch('use_itrust_tenent')}
                placeholder="例）東京都港区"
                onChange={onChange}
              />
            )}
          />
        </Grid.Item>
        <Grid.Item nextRow flexDirection="column">
          <Label>有効日数</Label>
          <Controller
            name="identification_validity_days"
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextBox
                syncValue={value != null ? String(value) : ''}
                fullWidth
                disabled={
                  !isInputAllowed('identification_validity_days') || !watch('use_itrust_tenent')
                }
                placeholder="365"
                onChange={(e) => {
                  const v = e.target.value;
                  if (/^\d*$/.test(v)) {
                    onChange(v === '' ? null : Number(v));
                  }
                }}
              />
            )}
          />
        </Grid.Item>
      </Grid.Item>
    </FormGrid>
  );
};

export default AdditionalSettingsForm;
